import { useMemo, useState } from 'react';
import { X, Pencil, Archive as ArchiveIcon } from 'lucide-react';
import { Art, BeltRank, TechniqueType, Uniform } from '../types';
import type { Technique, Category } from '../types';
import { ARTS, ART_META, getTierLabelsFor, impliedUniform, sortArts, techArts, tierRungs } from '../data/arts';
import { categoryInArts } from '../data/categories';
import { authorProfileUrl } from '../utils/video';
import { NO_RULE, ruleFor, useRules } from '../utils/legality';
import SuggestInput from './SuggestInput';

interface TechniqueInfoModalProps {
  tech: Technique;
  categories: Category[];
  /** Everything already in the library — the free-text rows offer its spellings
   *  as suggestions, so an edit lands on an existing name rather than beside it. */
  existing?: Technique[];
  /** Admins can edit fields inline (saved via the updateTechnique function). */
  canEdit: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent can reflect the new values. */
  onSaved: (patch: Partial<Technique>) => void;
  /** Admin-only: archive the technique (moves it into `archived_techniques`).
      When provided, an Archive button sits next to Save while editing. */
  onArchive?: () => Promise<unknown> | void;
  /** Set for a technique that only exists on this device (one the user added).
      updateTechnique only knows the shared library, so the edit is persisted
      through this instead of over the wire. */
  onSaveLocal?: (patch: Partial<Technique>) => void;
}

// Technique details modal. Read-only for everyone; admins get a pencil that
// flips name / type / position / level into editable inputs (Save / Cancel).
// Saving calls the updateTechnique Cloud Function — no need for the audit panel.
export default function TechniqueInfoModal({ tech, categories, existing = [], canEdit, onClose, onSaved, onArchive, onSaveLocal }: TechniqueInfoModalProps) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tech.name);
  const [categoryId, setCategoryId] = useState(tech.categoryId);
  const [tier, setTier] = useState<BeltRank>(tech.tier);
  const [type, setType] = useState<TechniqueType>(tech.type);
  // Missing uniform is treated as Gi throughout the app (legacy data).
  const [uniform, setUniform] = useState<Uniform>(tech.uniform ?? Uniform.Gi);
  // Missing arts is treated as BJJ (legacy data).
  const [arts, setArts] = useState<Art[]>(techArts(tech));
  // Level names come from the clip's own sports, not whatever the app is
  // currently browsing — a wrestling clip opened from a Home rail while the
  // library is set to BJJ must not read "Purple Belt". Follows the chips while
  // editing, the same way the submit form does.
  const tierLabels = getTierLabelsFor(arts);

  const toggleArt = (a: Art) => {
    setArts((prev) => {
      if (prev.includes(a)) {
        // At least one art must stay selected.
        return prev.length > 1 ? prev.filter((x) => x !== a) : prev;
      }
      return sortArts([...prev, a]);
    });
  };
  const [author, setAuthor] = useState(tech.author ?? '');
  const [attribution, setAttribution] = useState(tech.attribution ?? '');
  const [context, setContext] = useState(tech.context ?? '');
  const [rule, setRule] = useState(tech.rule ?? '');
  // The spellings already in use, offered as you type — same idea as the
  // submit form: match what's there instead of adding a near-duplicate.
  const suggestions = useMemo(() => {
    const uniq = (pick: (t: Technique) => string | null | undefined) =>
      [...new Set(existing.map((t) => pick(t)?.trim()).filter((v): v is string => !!v))]
        .sort((a, b) => a.localeCompare(b));
    return {
      names: uniq((t) => t.name),
      contexts: uniq((t) => t.context),
      authors: uniq((t) => t.author),
      attributions: uniq((t) => t.attribution),
    };
  }, [existing]);
  const rules = useRules();
  /** The row this technique falls under by name alone, and the one it shows. */
  const autoRule = useMemo(() => ruleFor({ ...tech, rule: undefined }, rules), [tech, rules]);
  const matchedRule = useMemo(() => ruleFor(tech, rules), [tech, rules]);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState('');

  const typeLabel = Object.entries(TechniqueType).find(([, v]) => v === tech.type)?.[0] ?? tech.type;
  const positionLabel = categories.find((c) => c.id === tech.categoryId)?.label ?? tech.categoryId;

  // Same scoping as the submit form: only positions belonging to the clip's
  // sports are offered. The one it's already filed under always stays in the
  // list even when it falls outside them — a legacy wrestling clip sitting in
  // Half Guard has to stay visible and re-filable, not silently reassigned.
  const positionOptions = categories.filter(
    (c) => c.id === categoryId || categoryInArts(c, arts)
  );

  // Also the same as the submit form: Judo is a gi sport and Wrestling a no-gi
  // one, so for those the uniform isn't a real field — the sports already
  // answer it. Hide the row and save the implied value rather than whatever
  // the (possibly legacy-wrong) stored one says.
  const implied = impliedUniform(arts);
  const chosenUniform = implied ?? uniform;

  const startEdit = () => {
    setName(tech.name);
    setCategoryId(tech.categoryId);
    setTier(tech.tier);
    setType(tech.type);
    setUniform(tech.uniform ?? Uniform.Gi);
    setArts(techArts(tech));
    setAuthor(tech.author ?? '');
    setAttribution(tech.attribution ?? '');
    setContext(tech.context ?? '');
    setRule(tech.rule ?? '');
    setError('');
    setEditing(true);
  };
  const archive = async () => {
    if (!onArchive || archiving) return;
    setArchiving(true);
    setError('');
    try {
      await onArchive();
      onClose();
    } catch (e) {
      console.error('Failed to archive technique:', e);
      setError('Could not archive.');
      setArchiving(false);
    }
  };

  const save = async () => {
    const n = name.trim();
    if (!n || saving) return;
    const a = author.trim().replace(/^@/, '') || null;
    const attr = attribution.trim() || null;
    const ctx = context.trim() || null;
    setSaving(true);
    setError('');
    const patch: Partial<Technique> = { name: n, categoryId, tier, type, uniform: chosenUniform, arts, author: a ?? undefined, attribution: attr ?? undefined, context: ctx ?? undefined, rule: rule || undefined };
    // A device-local technique has no document behind it, so saving it over the
    // wire comes back "not found" — which is what editing your own added clip
    // used to do.
    if (onSaveLocal) {
      onSaveLocal(patch);
      onSaved(patch);
      setEditing(false);
      setSaving(false);
      return;
    }
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { getApp } = await import('firebase/app');
      const fn = httpsCallable(getFunctions(getApp()), 'updateTechnique');
      await fn({ techniqueId: tech.id, name: n, categoryId, tier, type, uniform: chosenUniform, arts, author: a, attribution: attr, context: ctx, rule: rule || null });
      onSaved(patch);
      setEditing(false);
    } catch (e) {
      console.error('Failed to update technique:', e);
      setError('Could not save changes.');
    }
    setSaving(false);
  };

  return (
    <div className="info-modal-backdrop" onClick={onClose}>
      <div className="info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="info-modal-header">
          <span className="info-modal-title">Technique Details</span>
          <div className="info-modal-head-actions">
            {canEdit && !editing && (
              <button className="info-modal-edit" onClick={startEdit} aria-label="Edit technique" title="Edit technique">
                <Pencil size={15} />
              </button>
            )}
            <button className="info-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
          </div>
        </div>

        <table className="info-table">
          <tbody>
            <tr>
              <th>Sports</th>
              <td>
                {editing ? (
                  <div className="modal-art-chips" style={{ margin: 0 }}>
                    {ARTS.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        className={`category-filter-chip${arts.includes(a.id) ? ' category-filter-chip--active' : ''}`}
                        onClick={() => toggleArt(a.id)}
                      >
                        {a.shortLabel}
                      </button>
                    ))}
                  </div>
                ) : techArts(tech).map((a) => ART_META[a].shortLabel).join(', ')}
              </td>
            </tr>
            <tr>
              <th>Name</th>
              <td>
                {editing ? (
                  <SuggestInput
                    className="info-edit-input"
                    value={name}
                    onChange={setName}
                    options={suggestions.names}
                    placeholder="Technique name"
                    ariaLabel="Technique name"
                    autoFocus
                  />
                ) : tech.name}
              </td>
            </tr>
            {/* The entry this clip teaches the move from. Kept off the name on
                purpose, so every clip of the move stays one card and this is
                what tells the versions apart. */}
            <tr>
              <th>Context</th>
              <td>
                {editing ? (
                  <SuggestInput
                    className="info-edit-input"
                    value={context}
                    onChange={setContext}
                    options={suggestions.contexts}
                    placeholder="e.g. From Failed Arm Drag"
                    ariaLabel="Technique context"
                  />
                ) : tech.context ? (
                  tech.context
                ) : (
                  <span className="info-modal-empty">—</span>
                )}
              </td>
            </tr>
            <tr>
              <th>Position</th>
              <td>
                {editing ? (
                  <select className="info-edit-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                    {positionOptions.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                ) : positionLabel}
              </td>
            </tr>
            <tr>
              <th>Type</th>
              <td>
                {editing ? (
                  <select className="info-edit-select" value={type} onChange={(e) => setType(e.target.value as TechniqueType)}>
                    {Object.entries(TechniqueType).map(([label, v]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                ) : typeLabel}
              </td>
            </tr>
            <tr>
              <th>Level</th>
              <td>
                {editing ? (
                  <select className="info-edit-select" value={tier} onChange={(e) => setTier(Number(e.target.value) as BeltRank)}>
                    {tierRungs(tierLabels).map((r) => (
                      <option key={r.tier} value={r.tier}>{r.label}</option>
                    ))}
                  </select>
                ) : tierLabels[tech.tier]}
              </td>
            </tr>
            {!implied && (
              <tr>
                <th>Uniform</th>
                <td>
                  {editing ? (
                    <select className="info-edit-select" value={uniform} onChange={(e) => setUniform(e.target.value as Uniform)}>
                      <option value={Uniform.Gi}>Gi</option>
                      <option value={Uniform.NoGi}>No-Gi</option>
                    </select>
                  ) : (tech.uniform === Uniform.NoGi ? 'No-Gi' : 'Gi')}
                </td>
              </tr>
            )}
            <tr>
              <th>Author</th>
              <td>
                {editing ? (
                  <SuggestInput
                    className="info-edit-input"
                    value={author}
                    onChange={setAuthor}
                    options={suggestions.authors}
                    prefix="@"
                    placeholder="@handle"
                    ariaLabel="Author handle"
                  />
                ) : tech.author ? (
                  <a
                    href={authorProfileUrl(tech.instagramUrl, tech.author)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="info-modal-author-link"
                  >@{tech.author}</a>
                ) : (
                  <span className="info-modal-empty">—</span>
                )}
              </td>
            </tr>
            <tr>
              <th>Attribution</th>
              <td>
                {editing ? (
                  <SuggestInput
                    className="info-edit-input"
                    value={attribution}
                    onChange={setAttribution}
                    options={suggestions.attributions}
                    placeholder="e.g. Lachlan Giles"
                    ariaLabel="Technique attribution"
                  />
                ) : tech.attribution ? (
                  tech.attribution
                ) : (
                  <span className="info-modal-empty">—</span>
                )}
              </td>
            </tr>
            {(editing ? canEdit : !!matchedRule) && (
              <tr>
                <th>Rules</th>
                <td>
                  {editing ? (
                    <select className="info-edit-input" value={rule} onChange={(e) => setRule(e.target.value)} aria-label="Sport rule">
                      <option value="">{autoRule ? `Auto: ${autoRule.name}` : 'None'}</option>
                      {autoRule && <option value={NO_RULE}>None (ignore the name)</option>}
                      {rules.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  ) : matchedRule && (
                    <span className="info-modal-rules">
                      <span className="info-rule-name">{matchedRule.name}{tech.rule ? '' : ' (by name)'}</span>
                      {ARTS.filter((a) => matchedRule.legality[a.id]).map((a) => (
                        <span key={a.id} className={`info-rule${matchedRule.legality[a.id] === 'banned' ? ' info-rule--banned' : ''}`}>
                          <b>{a.shortLabel}</b> {matchedRule.legality[a.id] === 'banned' ? 'Banned' : matchedRule.legality[a.id]}
                        </span>
                      ))}
                      {ARTS.every((a) => !matchedRule.legality[a.id]) && <span className="info-rule">Legal everywhere</span>}
                    </span>
                  )}
                </td>
              </tr>
            )}
            {/* The move's other names. One today (the Japanese name); drawn as
                chips so a list reads the same way when there are more. */}
            {(tech.japaneseName || tech.aliases?.length) && !editing ? (
              <tr>
                <th>Also known as</th>
                <td>
                  <span className="info-modal-names">
                    {[tech.japaneseName, ...(tech.aliases ?? [])].filter(Boolean).map((n) => <span key={n}>{n}</span>)}
                  </span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>

        {editing && (
          <div className="info-modal-actions">
            {error && <span className="info-modal-error">{error}</span>}
            {onArchive && (
              <button
                className="modal-btn modal-btn--danger"
                onClick={archive}
                disabled={saving || archiving}
                title="Move to archive"
              >
                <ArchiveIcon size={14} />
                {archiving ? 'Archiving…' : 'Archive'}
              </button>
            )}
            <button className="modal-btn modal-btn--add" onClick={save} disabled={!name.trim() || saving || archiving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
